'use client';

import { createContext, useContext } from 'react';

import { PageLoader } from '@crehana/compass.ui/v2';

import { GlobalInternalErrorBoundary } from '@/shared/components/GlobalErrorBoundary.client';
import { NotAuthorizedPage } from '@/shared/components/NotAuthorizedPage';
import { useCrehanaCurrentSession } from '@/shared/hooks/useCrehanaCurrentSession';
import { useLearningToken } from '@/shared/hooks/useLearningToken';
import { useTalentToken } from '@/shared/hooks/useTalentToken';

import { useGetModuleNames } from './hooks/useGetModulesName';
import { type TTalentUser, useGetUserData } from './hooks/useGetUserData';
import { useIsAuthorizedToLoadPage } from './hooks/useIsAuthorizedToLoadPage';

/* context state */
type TContextState = {
  user: TTalentUser;
  getModuleName: (label: string) => string | undefined;
  employeeId: number;
  companyId: number;
  userParentId: number | null;
  // These token is used to authenticate the request in endpoints that requires to be authenticated in the flex-java acsendo application
  // for example: the pid creation activity endpoint, pid file upload, etc
  flexSessionId: string;
  learningToken?: string;
  talentToken?: string;
  crehanaSession?: {
    organizationId: number;
    userId: number;
  };
};

const Context = createContext<TContextState | undefined>(undefined);

// const LS_USER_KEY = 'talent.user';
//company_Id=17278&user_Id=1096053&employee_Id=1184180&user_Parent_Id=1196226
type TTalentAuthContextClientProviderProps = {
  children: React.ReactNode;
  // these props are only used for testing or storybook environments
  companyId?: number;
  userId?: number;
  employeeId?: number;
  userParentId?: number;
  flexSessionId?: string;
};

/**
 * Use the `useTalentAuth` to get the data for the current user.
 * ````tsx
 * import { useTalentAuth } from '@/shared/contexts/TalentAuth';
 *
 * const MyComponent = () => {
 *  const { user } = useTalentAuth()
 *  ...
 * }
 * ````
 */
export const TalentAuthContextClientProvider: React.FC<
  TTalentAuthContextClientProviderProps
> = ({
  children,

  // these props are only used for testing or storybook environments
  companyId,
  userId,
  employeeId,
  userParentId,
}) => {
  /* obtains flex token */
  const {
    isAuthorized,
    loading: loadingIsAuthorized,
    flexSessionId,
  } = useIsAuthorizedToLoadPage();

  /* obtains talent user data */
  const {
    user,
    queryParamsIds,
    loading: loadingUserData,
    error: errorToGetUserData,
  } = useGetUserData({
    companyId,
    userId,
    employeeId,
    userParentId,
  });

  /* obtain talent module names */
  const { loading: loadingModulesName, moduleNames } = useGetModuleNames({
    companyId,
    user,
  });

  /* return module name by label */
  const getModuleName = (label: string) => {
    return moduleNames.find(module => {
      return module.label === label;
    })?.value;
  };

  /* generate learning token */
  const { token: learningToken, isPending: isPendingLearningToken } =
    useLearningToken({ user });

  /* generate talent token */
  const {
    token: talentToken,
    isPending: isPendingTalentToken,
    isError: isErrorTalentToken,
    error: errorTalentToken,
  } = useTalentToken();

  /* obtains crehana session */
  const { data: crehanaSession, loading: isLoadingCrehanaSession } =
    useCrehanaCurrentSession({
      // this query dependes on the  window.crehApolloHeaders so we need to skip this until the tokens have loaded
      skip: isPendingLearningToken,
    });

  /* loading state */
  if (
    loadingUserData ||
    loadingModulesName ||
    loadingIsAuthorized ||
    isPendingLearningToken ||
    isPendingTalentToken ||
    isLoadingCrehanaSession
  ) {
    return <PageLoader />;
  }

  /* not authorized state */
  if (!isAuthorized || !flexSessionId) {
    return <NotAuthorizedPage />;
  }

  /* error state */
  if (errorToGetUserData || !user || isErrorTalentToken) {
    const error = errorToGetUserData || errorTalentToken;
    console.error(error);

    return (
      <GlobalInternalErrorBoundary
        error={{
          name: error?.name as string,
          message: error?.message as string,
        }}
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        // biome-ignore lint/suspicious/noEmptyBlockStatements: intentional empty block
        reset={() => {}}
      />
    );
  }

  /* context */
  return (
    <Context.Provider
      value={{
        user,
        getModuleName,
        employeeId: queryParamsIds.employee_id,
        companyId: queryParamsIds.company_id,
        userParentId: queryParamsIds.user_parent_id,
        flexSessionId,
        learningToken,
        talentToken,
        crehanaSession,
      }}
    >
      {children}
    </Context.Provider>
  );
};

export const useTalentAuth = () => {
  const context = useContext(Context);
  if (context === undefined) {
    throw new Error(
      'useTalentAuth must be used within the TalentAuthContextClientProvider.',
    );
  }
  return context;
};
